import { useEffect, useRef, useState } from 'react'

import { useAction } from '../../hooks/useAction'
import { Modal } from '../Modal'

export interface AwsSsoDeviceAuth {
  region: string
  client_id: string
  client_secret: string
  device_code: string
  user_code: string
  verification_uri_complete: string
  verification_uri: string
  interval: number
  expires_in: number
}

export interface AwsSsoTokenResult {
  status: string
  access_token: string | null
  expires_in: number | null
  reason: string | null
}

export interface AwsSsoAccount {
  account_id: string
  account_name: string
  email: string | null
  roles: string[]
}

export interface AwsSsoRoleCredentials {
  access_key_id: string
  secret_access_key: string
  session_token: string
  expiration: number | null
}

export interface AwsSsoResolvedAccount {
  account: AwsSsoAccount
  role_name: string
  credentials: AwsSsoRoleCredentials
  expires_at?: string | null
}

export interface AwsSsoProvisionedAccessKeys {
  access_key_id: string
  secret_access_key: string
  expiration: string
  user_name: string
}

export interface AwsSsoConnectionApi {
  start: (startUrl: string, region: string) => Promise<AwsSsoDeviceAuth>
  token: (p: { clientId: string; clientSecret: string; deviceCode: string; region: string }) => Promise<AwsSsoTokenResult>
  accounts: (accessToken: string, region: string) => Promise<{ accounts: AwsSsoAccount[] }>
  credentials: (accessToken: string, region: string, accountId: string, roleName: string) => Promise<AwsSsoRoleCredentials>
  provisionAccessKeys: (p: {
    tempAccessKey: string
    tempSecretKey: string
    tempSessionToken: string
    accountId: string
    expirationDays: number
  }) => Promise<AwsSsoProvisionedAccessKeys>
}

interface Props {
  api: AwsSsoConnectionApi
  onResolvedAccounts: (accounts: AwsSsoResolvedAccount[]) => void
  buttonLabel?: string
  buttonTitle?: string
  modalTitle?: string
  testIdPrefix?: string
  autoOpenSignal?: number
  hideTrigger?: boolean
}

type Step = 'config' | 'awaiting' | 'select' | 'authorize'

const inputStyle = { width: '100%' } as const

/** A failure after sign-in usually means the short-lived SSO session lapsed. */
const sessionExpired = (e: unknown) => `${String(e)} - the SSO session may have expired; sign in again.`
/** Key provisioning needs IAM writes the SSO role may not have. */
const iamWriteDenied = (e: unknown) =>
  `${String(e)} - confirm the selected SSO role can create IAM users, attach policies, and create access keys.`

export function AwsSsoConnectionWizard({
  api,
  onResolvedAccounts,
  buttonLabel = 'Sign in with SSO',
  buttonTitle = 'Fetch temporary credentials from AWS IAM Identity Center',
  modalTitle = 'Sign in with AWS SSO',
  testIdPrefix = 'aws-sso',
  autoOpenSignal = 0,
  hideTrigger = false,
}: Props) {
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState<Step>('config')
  const [startUrl, setStartUrl] = useState('')
  const [region, setRegion] = useState('')
  const { busy, error, setError, run } = useAction()
  const [device, setDevice] = useState<AwsSsoDeviceAuth | null>(null)
  const accessTokenRef = useRef<string | null>(null)
  const activeRegionRef = useRef<string>('')
  const [accounts, setAccounts] = useState<AwsSsoAccount[]>([])
  const [selected, setSelected] = useState<Record<string, string>>({})
  const [pendingAccounts, setPendingAccounts] = useState<AwsSsoResolvedAccount[]>([])
  const [createLongTermKeys, setCreateLongTermKeys] = useState(false)
  const [expirationDays, setExpirationDays] = useState(180)
  const pollRef = useRef<number | null>(null)

  const stopPoll = () => {
    if (pollRef.current !== null) {
      window.clearTimeout(pollRef.current)
      pollRef.current = null
    }
  }

  const reset = () => {
    stopPoll()
    setStep('config')
    setError(null)
    setDevice(null)
    accessTokenRef.current = null
    activeRegionRef.current = ''
    setAccounts([])
    setSelected({})
    setPendingAccounts([])
    setCreateLongTermKeys(false)
    setExpirationDays(180)
  }

  const close = () => {
    stopPoll()
    setOpen(false)
  }

  useEffect(() => () => stopPoll(), [])

  useEffect(() => {
    if (autoOpenSignal <= 0) return
    reset()
    setOpen(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenSignal])

  const loadAccounts = async (token: string) => {
    const r = await run(() => api.accounts(token, activeRegionRef.current), {
      errorMessage: sessionExpired,
    })
    if (r) {
      setAccounts(r.accounts)
      setStep('select')
    } else {
      setStep('config')
    }
  }

  const beginPoll = (d: AwsSsoDeviceAuth) => {
    const tick = async () => {
      try {
        const r = await api.token({
          clientId: d.client_id,
          clientSecret: d.client_secret,
          deviceCode: d.device_code,
          region: d.region,
        })
        if (r.status === 'complete' && r.access_token) {
          accessTokenRef.current = r.access_token
          await loadAccounts(r.access_token)
          return
        }
        if (r.status === 'expired' || r.status === 'error') {
          setError(r.reason || 'SSO sign-in failed. Start over.')
          setStep('config')
          return
        }
        const wait = (r.status === 'slow_down' ? d.interval + 5 : d.interval) * 1000
        pollRef.current = window.setTimeout(tick, wait)
      } catch (e) {
        setError(String(e))
        setStep('config')
      }
    }
    pollRef.current = window.setTimeout(tick, d.interval * 1000)
  }

  const start = async () => {
    if (!startUrl.trim()) {
      setError('Enter your AWS SSO start URL (e.g. https://my-company.awsapps.com/start).')
      return
    }
    await run(async () => {
      const d = await api.start(startUrl.trim(), region.trim())
      activeRegionRef.current = d.region
      setDevice(d)
      setRegion(d.region)
      setStep('awaiting')
      window.open(d.verification_uri_complete, '_blank', 'noopener,noreferrer')
      beginPoll(d)
    })
  }

  const toggle = (acc: AwsSsoAccount) => {
    setSelected((s) => {
      const next = { ...s }
      if (acc.account_id in next) delete next[acc.account_id]
      else next[acc.account_id] = acc.roles[0] ?? ''
      return next
    })
  }

  const prepareAuthorization = async () => {
    const token = accessTokenRef.current
    if (!token) return
    const chosen = Object.entries(selected).filter(([, role]) => role)
    if (chosen.length === 0) {
      setError('Select at least one account.')
      return
    }
    await run(async () => {
      const pending: AwsSsoResolvedAccount[] = []
      for (const [accountId, role] of chosen) {
        const credentials = await api.credentials(token, activeRegionRef.current, accountId, role)
        const account = accounts.find((a) => a.account_id === accountId)
        if (account) {
          pending.push({ account, role_name: role, credentials })
        }
      }
      setPendingAccounts(pending)
      setStep('authorize')
    }, { errorMessage: sessionExpired })
  }

  const authorizeAccess = async () => {
    if (pendingAccounts.length === 0) {
      setError('Select at least one account.')
      setStep('select')
      return
    }
    await run(async () => {
      if (!createLongTermKeys) {
        onResolvedAccounts(pendingAccounts)
        close()
        reset()
        return
      }
      const resolved: AwsSsoResolvedAccount[] = []
      for (const pending of pendingAccounts) {
        const provisioned = await api.provisionAccessKeys({
          tempAccessKey: pending.credentials.access_key_id,
          tempSecretKey: pending.credentials.secret_access_key,
          tempSessionToken: pending.credentials.session_token,
          accountId: pending.account.account_id,
          expirationDays,
        })
        resolved.push({
          account: pending.account,
          role_name: pending.role_name,
          expires_at: provisioned.expiration,
          credentials: {
            access_key_id: provisioned.access_key_id,
            secret_access_key: provisioned.secret_access_key,
            session_token: '',
            expiration: null,
          },
        })
      }
      onResolvedAccounts(resolved)
      close()
      reset()
    }, { errorMessage: iamWriteDenied })
  }

  const authorizeButtonLabel = (() => {
    if (busy) return createLongTermKeys ? 'Authorizing...' : 'Using temporary credentials...'
    return createLongTermKeys ? 'Authorize and create access keys' : 'Use temporary credentials'
  })()

  return (
    <>
      {(!hideTrigger || !open) && (
        <button
          type="button"
          className="primary"
          onClick={() => { reset(); setOpen(true) }}
          data-testid={`${testIdPrefix}-open-btn`}
          title={buttonTitle}
        >
          {buttonLabel}
        </button>
      )}

      <Modal open={open} onClose={close} title={modalTitle} ariaLabel={modalTitle} testId={`${testIdPrefix}-modal`} maxWidth={620} closeOnOverlayClick={false}>
        {error && <div className="error" role="alert">{error}</div>}

        {step === 'config' && (
          <div data-testid={`${testIdPrefix}-config`}>
            <p style={{ fontSize: '0.88rem', color: 'var(--muted)', marginTop: 0 }}>
              Enter your IAM Identity Center <strong>start URL</strong>. Leave the region blank
              to auto-detect the IAM Identity Center region.
            </p>
            <label>
              SSO start URL
              <input
                type="url"
                value={startUrl}
                onChange={(e) => setStartUrl(e.target.value)}
                placeholder="https://my-company.awsapps.com/start"
                style={inputStyle}
                data-testid={`${testIdPrefix}-start-url`}
              />
            </label>
            <label style={{ display: 'block', marginTop: '0.6rem' }}>
              SSO region <span style={{ color: 'var(--muted)' }}>(leave blank to auto-detect)</span>
              <input
                type="text"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                placeholder="Auto-detect"
                style={inputStyle}
                data-testid={`${testIdPrefix}-region`}
              />
            </label>
            <button type="button" className="primary" onClick={start} disabled={busy} style={{ marginTop: '0.85rem' }} data-testid={`${testIdPrefix}-start-btn`}>
              {busy ? 'Starting...' : 'Sign in'}
            </button>
          </div>
        )}

        {step === 'awaiting' && device && (
          <div data-testid={`${testIdPrefix}-awaiting`}>
            <p style={{ marginTop: 0 }}>
              A browser tab should have opened to approve this sign-in. If not,{' '}
              <a href={device.verification_uri_complete} target="_blank" rel="noopener noreferrer">open it here</a>.
            </p>
            <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>Using IAM Identity Center region: {device.region}</p>
            <p style={{ fontSize: '0.9rem' }}>
              Confirm this code matches what AWS shows:{' '}
              <strong style={{ fontFamily: 'ui-monospace, monospace', fontSize: '1.1rem' }} data-testid={`${testIdPrefix}-user-code`}>
                {device.user_code}
              </strong>
            </p>
            <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>Waiting for you to approve in the browser...</p>
          </div>
        )}

        {step === 'select' && (
          <div data-testid={`${testIdPrefix}-select`}>
            <p style={{ marginTop: 0, fontSize: '0.88rem', color: 'var(--muted)' }}>
              Select the accounts to import and the role to assume in each.
            </p>
            {accounts.length === 0 && <div className="empty-state">No accounts available for this SSO user.</div>}
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: 320, overflowY: 'auto' }}>
              {accounts.map((a) => {
                const checked = a.account_id in selected
                return (
                  <li key={a.account_id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0' }} data-testid={`${testIdPrefix}-account-${a.account_id}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggle(a)} disabled={a.roles.length === 0} />
                    <span style={{ flex: 1 }}>
                      <strong>{a.account_name}</strong>{' '}
                      <span style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>{a.account_id}</span>
                    </span>
                    <select
                      value={selected[a.account_id] ?? a.roles[0] ?? ''}
                      onChange={(e) => setSelected((s) => ({ ...s, [a.account_id]: e.target.value }))}
                      disabled={!checked || a.roles.length === 0}
                      style={{ maxWidth: 180 }}
                    >
                      {a.roles.length === 0 ? (
                        <option value="">(no roles)</option>
                      ) : (
                        a.roles.map((r) => <option key={r} value={r}>{r}</option>)
                      )}
                    </select>
                  </li>
                )
              })}
            </ul>
            <button type="button" className="primary" onClick={prepareAuthorization} disabled={busy} style={{ marginTop: '0.85rem' }} data-testid={`${testIdPrefix}-add-btn`}>
              {busy ? 'Preparing...' : `Continue with ${Object.keys(selected).length || ''} account${Object.keys(selected).length === 1 ? '' : 's'}`.trim()}
            </button>
          </div>
        )}

        {step === 'authorize' && (
          <div data-testid={`${testIdPrefix}-authorize`}>
            <p style={{ marginTop: 0, fontSize: '0.88rem', color: 'var(--muted)' }}>
              SSO returned temporary credentials for the selected accounts. Use them for this scan,
              or optionally create read-only AllCode access keys if the customer allows IAM writes.
            </p>
            <label style={{ display: 'flex', gap: '0.45rem', alignItems: 'flex-start', marginTop: '0.65rem' }}>
              <input
                type="checkbox"
                checked={createLongTermKeys}
                onChange={(e) => setCreateLongTermKeys(e.target.checked)}
                data-testid={`${testIdPrefix}-create-long-term-keys`}
              />
              <span>
                Create longer-term read-only AllCode access keys for future scans
                <span style={{ display: 'block', color: 'var(--muted)', fontSize: '0.82rem' }}>
                  Leave unchecked when the customer only grants temporary SSO access.
                </span>
              </span>
            </label>
            {createLongTermKeys && (
              <label style={{ display: 'block', marginTop: '0.65rem' }}>
                Auto-expire access after
                <select
                  value={expirationDays}
                  onChange={(e) => setExpirationDays(Number(e.target.value))}
                  style={inputStyle}
                  data-testid={`${testIdPrefix}-expiration-days`}
                >
                  <option value={90}>3 months</option>
                  <option value={180}>6 months</option>
                </select>
              </label>
            )}
            <ul style={{ paddingLeft: '1.1rem', color: 'var(--muted)', fontSize: '0.86rem' }}>
              {pendingAccounts.map(({ account, role_name }) => (
                <li key={account.account_id}>
                  {account.account_name} ({account.account_id}) via {role_name}
                </li>
              ))}
            </ul>
            <p style={{ color: 'var(--muted)', fontSize: '0.82rem' }}>
              {createLongTermKeys ? (
                <>
                  This creates or updates the <code>ai-assessment-readonly</code> role and key
                  identity, attaches AWS read-only policies, and adds an inline deny policy after
                  the selected expiration date.
                </>
              ) : (
                'No IAM users, roles, policies, or access keys will be created. The scan will use the temporary SSO credentials AWS just issued.'
              )}
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.85rem' }}>
              <button type="button" onClick={() => setStep('select')} disabled={busy}>
                Back
              </button>
              <button type="button" className="primary" onClick={authorizeAccess} disabled={busy} data-testid={`${testIdPrefix}-authorize-btn`}>
                {authorizeButtonLabel}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  )
}
